import type { Product } from '../types';

const PLACEHOLDER_IMG =
  'https://images.unsplash.com/photo-1560393464-5c69a73c5770?w=400&h=400&fit=crop';

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

export function normalizeProduct(raw: unknown): Product | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = o.id != null ? String(o.id) : '';
  if (!id) return null;

  const name = typeof o.name === 'string' ? o.name : 'Product';
  const price = num(o.price ?? o.unitPrice);
  const originalRaw = o.originalPrice ?? o.compareAtPrice ?? o.listPrice;
  const originalPrice =
    originalRaw !== undefined && originalRaw !== null && originalRaw !== ''
      ? num(originalRaw)
      : undefined;

  let image = '';
  if (typeof o.image === 'string' && o.image) image = o.image;
  else if (typeof o.imageUrl === 'string' && o.imageUrl) image = o.imageUrl;
  else if (Array.isArray(o.images) && o.images.length > 0 && typeof o.images[0] === 'string') {
    image = o.images[0];
  }
  if (!image) image = PLACEHOLDER_IMG;

  const description = typeof o.description === 'string' ? o.description : '';
  const category = typeof o.category === 'string' ? o.category : 'General';

  const rating = num(o.rating, 0);
  const count = o._count;
  const reviewCount =
    typeof count === 'object' && count !== null && 'reviews' in count
      ? num((count as { reviews: unknown }).reviews, 0)
      : 0;
  const reviews = num(o.reviews ?? o.reviewCount, reviewCount);

  let inStock = true;
  if (typeof o.inStock === 'boolean') inStock = o.inStock;
  else if (typeof o.stock === 'number') inStock = o.stock > 0;
  else if (typeof o.quantity === 'number') inStock = o.quantity > 0;

  const featured = Boolean(o.featured ?? o.isFeatured);
  const stock =
    o.stock !== undefined && o.stock !== null ? num(o.stock) : o.quantity !== undefined ? num(o.quantity) : undefined;

  const product: Product = {
    id,
    name,
    price,
    image,
    description,
    category,
    rating,
    reviews,
    inStock,
    featured,
  };

  if (stock !== undefined) {
    product.stock = stock;
  }

  if (originalPrice !== undefined && originalPrice > price) {
    product.originalPrice = originalPrice;
  }

  return product;
}

function unwrapArray(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (body && typeof body === 'object' && Array.isArray((body as { data: unknown }).data)) {
    return (body as { data: unknown[] }).data;
  }
  return [];
}

export function normalizeProductList(body: unknown): Product[] {
  return unwrapArray(body)
    .map(normalizeProduct)
    .filter((p): p is Product => p !== null);
}
